import {
  AdditiveBlending,
  BufferGeometry,
  Group,
  InstancedBufferAttribute,
  InstancedBufferGeometry,
  Mesh,
  MultiplyBlending,
  NormalBlending,
  PlaneGeometry,
  ShaderMaterial,
  type Blending,
  type Scene,
} from 'three';

/**
 * A pool of billboarded quads, each carrying sixteen floats of its own.
 *
 * Smoke, ink and pigment are all "many soft marks drifting and fading", and
 * writing that three times would produce three subtly different pools with
 * three separate bugs. What actually differs between the three materials is the
 * *fragment shader* and the rules that move the marks — so this owns the parts
 * that are genuinely the same and hands the rest to the renderer.
 *
 * Four `vec4` instance attributes plus the quad's own position and uv is six
 * attributes, well inside the sixteen this project holds itself to. They are
 * packed rather than named individually because each renderer means something
 * different by them, and a shared `aRadius` that one material read as a bleed
 * radius and another as a vortex strength would be worse than an index.
 */

/** How many floats each instance carries. Four `vec4`s. */
export const INSTANCE_FLOATS = 16;

export interface InstancedFieldOptions {
  readonly scene: Scene;
  readonly name: string;
  readonly capacity: number;
  readonly fragmentShader: string;
  readonly vertexShader?: string;
  readonly uniforms?: Record<string, { value: unknown }>;
  readonly blending?: Blending;
  readonly depthWrite?: boolean;
  /**
   * Whether the fragment shader emits colour already multiplied by alpha.
   *
   * Needed by anything that draws into a render target which is then composited
   * back: the target holds blended data, and compositing that as if it were
   * straight alpha multiplies the colour by the alpha a second time.
   */
  readonly premultipliedAlpha?: boolean;
  readonly renderOrder?: number;
}

/**
 * The default vertex shader.
 *
 * Billboards a unit quad at `aTransform.xy`, scaled by `.z` and rotated by `.w`.
 * A scale of zero collapses the quad to a point, which is how a dead instance
 * is hidden without touching the draw call or the buffer's length — the pool is
 * always drawn whole, and the shader decides what is there.
 */
const DEFAULT_VERTEX = /* glsl */ `
attribute vec4 aTransform;  // x, y, scale, rotation
attribute vec4 aLife;       // born, lifetime, seed, kind
attribute vec4 aColor;      // r, g, b, alpha
attribute vec4 aShape;      // four material-specific parameters

uniform float uTime;

varying vec2 vUv;
varying vec4 vLife;
varying vec4 vColor;
varying vec4 vShape;
varying float vAge;

void main() {
  vUv = uv;
  vLife = aLife;
  vColor = aColor;
  vShape = aShape;

  float lifetime = max(aLife.y, 0.0001);
  vAge = clamp((uTime - aLife.x) / lifetime, 0.0, 1.0);

  float dead = step(aTransform.z, 0.0);
  float alive = 1.0 - dead;

  float c = cos(aTransform.w);
  float s = sin(aTransform.w);
  vec2 local = position.xy * aTransform.z * alive;
  vec2 rotated = vec2(local.x * c - local.y * s, local.x * s + local.y * c);

  vec3 world = vec3(aTransform.xy + rotated, 0.0);
  gl_Position = projectionMatrix * modelViewMatrix * vec4(world, 1.0);
}
`;

export class InstancedField {
  readonly group = new Group();
  readonly material: ShaderMaterial;

  private readonly geometry: InstancedBufferGeometry;
  private readonly transform: InstancedBufferAttribute;
  private readonly life: InstancedBufferAttribute;
  private readonly colour: InstancedBufferAttribute;
  private readonly shape: InstancedBufferAttribute;
  private readonly mesh: Mesh;
  private readonly scene: Scene;
  private readonly source: BufferGeometry;

  private attached = false;
  private live = 0;

  readonly capacity: number;

  constructor(options: InstancedFieldOptions) {
    this.scene = options.scene;
    this.capacity = Math.max(1, options.capacity);

    this.source = new PlaneGeometry(1, 1);
    this.geometry = new InstancedBufferGeometry();
    this.geometry.index = this.source.index;
    this.geometry.attributes.position = this.source.attributes.position as never;
    this.geometry.attributes.uv = this.source.attributes.uv as never;

    this.transform = instanced(this.capacity, 4);
    this.life = instanced(this.capacity, 4);
    this.colour = instanced(this.capacity, 4);
    this.shape = instanced(this.capacity, 4);
    this.geometry.setAttribute('aTransform', this.transform);
    this.geometry.setAttribute('aLife', this.life);
    this.geometry.setAttribute('aColor', this.colour);
    this.geometry.setAttribute('aShape', this.shape);
    this.geometry.instanceCount = 0;

    this.material = new ShaderMaterial({
      vertexShader: options.vertexShader ?? DEFAULT_VERTEX,
      fragmentShader: options.fragmentShader,
      uniforms: { uTime: { value: 0 }, ...(options.uniforms ?? {}) },
      transparent: true,
      premultipliedAlpha: options.premultipliedAlpha ?? false,
      depthWrite: options.depthWrite ?? false,
      depthTest: false,
      blending: options.blending ?? NormalBlending,
    });

    this.mesh = new Mesh(this.geometry, this.material);
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = options.renderOrder ?? 10;
    this.group.name = options.name;
    this.group.add(this.mesh);
  }

  attach(): void {
    if (this.attached) return;
    this.scene.add(this.group);
    this.attached = true;
  }

  detach(): void {
    if (!this.attached) return;
    this.scene.remove(this.group);
    this.attached = false;
  }

  get liveCount(): number {
    return this.live;
  }

  set visible(value: boolean) {
    this.group.visible = value;
  }

  setUniform(name: string, value: unknown): void {
    const uniform = this.material.uniforms[name];
    if (uniform !== undefined) uniform.value = value;
  }

  setTime(time: number): void {
    this.setUniform('uTime', time);
  }

  /**
   * Writes one instance.
   *
   * `index` is the caller's slot, so the renderer owns its own pooling and this
   * owns none of it. Nothing is uploaded until `commit`.
   */
  write(
    index: number,
    transform: readonly [number, number, number, number],
    life: readonly [number, number, number, number],
    colour: readonly [number, number, number, number],
    shape: readonly [number, number, number, number],
  ): void {
    if (index < 0 || index >= this.capacity) return;
    set4(this.transform, index, transform);
    set4(this.life, index, life);
    set4(this.colour, index, colour);
    set4(this.shape, index, shape);
  }

  /** Hides a slot without disturbing its neighbours. */
  clearSlot(index: number): void {
    if (index < 0 || index >= this.capacity) return;
    // Scale zero: the vertex shader collapses the quad rather than drawing it.
    (this.transform.array as Float32Array)[index * 4 + 2] = 0;
  }

  clearAll(): void {
    (this.transform.array as Float32Array).fill(0);
    (this.colour.array as Float32Array).fill(0);
    this.live = 0;
    this.geometry.instanceCount = 0;
    this.markDirty();
  }

  /**
   * Uploads the frame's writes and sets how many instances to draw.
   *
   * One upload for the whole pool rather than one per mark: the buffers are
   * contiguous and the GPU would rather have them in a single transfer than in
   * four hundred small ones.
   */
  commit(liveCount: number): void {
    this.live = Math.max(0, Math.min(this.capacity, liveCount));
    this.geometry.instanceCount = this.live;
    this.markDirty();
  }

  private markDirty(): void {
    this.transform.needsUpdate = true;
    this.life.needsUpdate = true;
    this.colour.needsUpdate = true;
    this.shape.needsUpdate = true;
  }

  dispose(): void {
    this.detach();
    this.geometry.dispose();
    this.source.dispose();
    this.material.dispose();
    this.group.clear();
  }
}

function instanced(count: number, size: number): InstancedBufferAttribute {
  return new InstancedBufferAttribute(new Float32Array(count * size), size);
}

function set4(
  attribute: InstancedBufferAttribute,
  index: number,
  values: readonly [number, number, number, number],
): void {
  const array = attribute.array as Float32Array;
  const offset = index * 4;
  array[offset] = values[0];
  array[offset + 1] = values[1];
  array[offset + 2] = values[2];
  array[offset + 3] = values[3];
}

/** Blend mode names from the recipe, as three's constants. */
export function blendingFor(mode: string): Blending {
  switch (mode) {
    case 'add':
    case 'screen':
      return AdditiveBlending;
    case 'multiply':
      return MultiplyBlending;
    default:
      return NormalBlending;
  }
}

/**
 * Shared GLSL: value noise, fractal brownian motion and a curl field.
 *
 * Prepended to each material's fragment shader. Curl noise is what makes smoke
 * read as smoke rather than as drifting dots — it is divergence-free, so the
 * flow swirls and folds instead of spraying outward from wherever it started.
 */
export const NOISE_GLSL = /* glsl */ `
float hash21(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}

float valueNoise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  float a = hash21(i);
  float b = hash21(i + vec2(1.0, 0.0));
  float c = hash21(i + vec2(0.0, 1.0));
  float d = hash21(i + vec2(1.0, 1.0));
  return mix(mix(a, b, u.x), mix(c, d, u.x), u.y);
}

float fbm(vec2 p, int octaves) {
  float total = 0.0;
  float amplitude = 0.5;
  for (int i = 0; i < 6; i++) {
    if (i >= octaves) break;
    total += valueNoise(p) * amplitude;
    p *= 2.03;
    amplitude *= 0.5;
  }
  return total;
}
`;
